import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, Loader2, Fingerprint, IdCard, FileCheck2, PenLine, AlertTriangle, Upload, Check, Lock } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = { navy: '#2B4C7E', navy2: '#1E3A63', gold: '#C8A97E', cream: '#FDFBF7', green: '#7FA98C', ink: '#1C252E', mute: '#8D8880', line: '#E3E1DC', red: '#C0563F' };
const inp = 'w-full h-11 px-3 rounded-xl border border-[#E3E1DC] text-sm outline-none focus:border-[#2B4C7E]';

const WAIVER = 'I understand that pressing Release will send the deceased person’s saved savings account location, beneficiary instructions, and related legacy information to the selected beneficiaries. I understand EasyPassing does not distribute funds, does not guarantee legal beneficiary rights, and does not replace the bank, court, executor process, will, trust, or legal estate administration. I understand this action cannot be undone.';

export default function ExecutorRelease() {
  const [token, setToken] = useState(sessionStorage.getItem('exec_token') || '');
  const [step, setStep] = useState(0);
  const [ctx, setCtx] = useState(null);

  const H = () => ({ headers: { Authorization: `Bearer ${token}` } });

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/executor/context`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { setCtx(r.data); if (r.data.release?.release_status === 'released') setStep(99); else if (step === 0) setStep(1); })
      .catch(() => { setToken(''); sessionStorage.removeItem('exec_token'); });
  }, [token]); // eslint-disable-line

  if (!token) return <Login onAuth={(t) => { setToken(t); sessionStorage.setItem('exec_token', t); }} />;

  return (
    <div className="min-h-screen overflow-x-hidden py-8 px-4" style={{ background: C.cream }} data-testid="executor-release-page">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-3" style={{ background: C.navy }}><ShieldCheck className="h-7 w-7 text-white" /></div>
          <h1 className="font-display text-2xl font-bold" style={{ color: C.ink }}>Release Legacy Savings Information</h1>
          {ctx?.owner_name && <p className="text-sm mt-1" style={{ color: C.mute }}>for {ctx.owner_name}’s account</p>}
        </div>

        {step === 99 ? <Released /> : (
          <>
            <Steps step={step} />
            <div className="bg-white rounded-2xl border border-[#EDE7DC] p-6 mt-4">
              {step === 1 && <Biometric onNext={() => setStep(2)} />}
              {step === 2 && <IdUpload H={H} onNext={() => setStep(3)} />}
              {step === 3 && <DeathCheck H={H} onNext={() => setStep(4)} />}
              {step === 4 && <Waiver H={H} onNext={() => setStep(5)} />}
              {step === 5 && <Confirm H={H} ctx={ctx} onReleased={() => setStep(99)} />}
            </div>
          </>
        )}
        <p className="text-center text-xs mt-5" style={{ color: C.mute }}>The Release button sends information and instructions. It does not transfer money.</p>
      </div>
    </div>
  );
}

function Login({ onAuth }) {
  const [email, setEmail] = useState(''); const [code, setCode] = useState(''); const [sent, setSent] = useState(false); const [busy, setBusy] = useState(false);
  const req = async () => { if (!email) return; setBusy(true); try { await axios.post(`${API}/executor/request-code`, { email }); setSent(true); toast.success('If you are a named executor, a code was emailed to you.'); } catch { toast.error('Could not send code'); } finally { setBusy(false); } };
  const verify = async () => { setBusy(true); try { const r = await axios.post(`${API}/executor/verify-code`, { email, code }); onAuth(r.data.token); toast.success(`Welcome, ${r.data.owner_name ? 'executor for ' + r.data.owner_name : 'executor'}`); } catch (e) { toast.error(e?.response?.data?.detail || 'Invalid code'); } finally { setBusy(false); } };
  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: C.cream }} data-testid="executor-login">
      <div className="bg-white rounded-3xl border border-[#EDE7DC] p-8 w-full max-w-md">
        <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4" style={{ background: C.navy }}><Lock className="h-6 w-6 text-white" /></div>
        <h1 className="font-display text-2xl font-bold" style={{ color: C.ink }}>Executor secure login</h1>
        <p className="text-sm mt-1 mb-5" style={{ color: C.mute }}>Enter the email where you received your executor invitation. We’ll email you a secure code.</p>
        <label className="block text-xs font-semibold mb-1" style={{ color: '#57534E' }}>Email</label>
        <input value={email} onChange={(e) => setEmail(e.target.value)} className={inp} data-testid="exec-email" placeholder="you@example.com" />
        {!sent ? (
          <button onClick={req} disabled={busy} className="mt-4 w-full h-11 rounded-xl text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: C.navy }} data-testid="exec-send-code">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Send code</button>
        ) : (
          <>
            <label className="block text-xs font-semibold mb-1 mt-4" style={{ color: '#57534E' }}>6-digit code</label>
            <input value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} className={inp} data-testid="exec-code" placeholder="123456" />
            <button onClick={verify} disabled={busy} className="mt-4 w-full h-11 rounded-xl text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: C.navy }} data-testid="exec-verify">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Verify &amp; continue</button>
            <button onClick={req} className="mt-2 w-full text-xs" style={{ color: C.mute }}>Resend code</button>
          </>
        )}
      </div>
    </div>
  );
}

const STEP_LABELS = [['Verify', Fingerprint], ['ID', IdCard], ['Confirm', FileCheck2], ['Sign', PenLine], ['Release', ShieldCheck]];
function Steps({ step }) {
  return (
    <div className="flex items-center justify-between gap-1">
      {STEP_LABELS.map(([label, Icon], i) => {
        const n = i + 1; const done = step > n; const active = step === n;
        return (
          <div key={label} className="flex flex-col items-center flex-1 min-w-0">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: done ? C.green : active ? C.navy : '#E8EAEE', color: (done || active) ? '#fff' : C.mute }}>
              {done ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
            </div>
            <span className="text-[10px] mt-1 truncate" style={{ color: active ? C.navy : C.mute }}>{label}</span>
          </div>
        );
      })}
    </div>
  );
}

function Biometric({ onNext }) {
  const [busy, setBusy] = useState(false); const [status, setStatus] = useState(null);
  const start = async () => {
    setStatus('start');
    axios.post(`${API}/executor/release/start`, {}, { headers: { Authorization: `Bearer ${sessionStorage.getItem('exec_token')}` } }).catch(() => {});
  };
  useEffect(() => { start(); }, []); // eslint-disable-line
  const verify = async () => {
    setBusy(true);
    try {
      if (window.PublicKeyCredential) {
        try {
          await navigator.credentials.create({ publicKey: {
            challenge: Uint8Array.from(String(Date.now()), (c) => c.charCodeAt(0)),
            rp: { name: 'EasyPassing' }, user: { id: Uint8Array.from('executor', (c) => c.charCodeAt(0)), name: 'executor', displayName: 'Executor' },
            pubKeyCredParams: [{ type: 'public-key', alg: -7 }], authenticatorSelection: { userVerification: 'preferred' }, timeout: 30000,
          } });
          setStatus('verified'); toast.success('Device verification complete'); sessionStorage.setItem('exec_biometric', 'true');
        } catch { setStatus('unavailable'); sessionStorage.setItem('exec_biometric', 'false'); toast.info('Biometric unavailable — extra ID verification will be required.'); }
      } else { setStatus('unavailable'); sessionStorage.setItem('exec_biometric', 'false'); }
    } finally { setBusy(false); }
  };
  return (
    <div className="text-center">
      <Fingerprint className="h-10 w-10 mx-auto mb-3" style={{ color: C.navy }} />
      <h2 className="font-bold text-lg" style={{ color: C.ink }}>Biometric verification</h2>
      <p className="text-sm mt-1 mb-4" style={{ color: C.mute }}>Verify your identity with this device (Face ID, fingerprint, or device lock). If biometrics are unavailable, you’ll complete extra ID verification in the next step.</p>
      {status !== 'verified' && status !== 'unavailable' && <button onClick={verify} disabled={busy} className="w-full h-11 rounded-xl text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: C.navy }} data-testid="bio-verify">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Verify with this device</button>}
      {status === 'verified' && <p className="text-sm font-semibold flex items-center justify-center gap-2" style={{ color: C.green }}><Check className="h-4 w-4" /> Verified on this device</p>}
      {status === 'unavailable' && <p className="text-sm" style={{ color: C.gold }}>Biometrics unavailable — continue with government ID verification.</p>}
      {(status === 'verified' || status === 'unavailable') && <button onClick={onNext} className="mt-4 w-full h-11 rounded-xl border font-semibold" style={{ borderColor: C.navy, color: C.navy }} data-testid="bio-next">Continue</button>}
    </div>
  );
}

function FileField({ label, onPick, testid }) {
  const [name, setName] = useState('');
  return (
    <label className="block border border-dashed border-[#CFD6DE] rounded-xl p-3 cursor-pointer" data-testid={testid}>
      <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { const f = e.target.files[0]; if (f) { setName(f.name); onPick(f); } }} />
      <div className="flex items-center gap-2 text-sm" style={{ color: name ? C.green : C.navy }}>{name ? <Check className="h-4 w-4" /> : <Upload className="h-4 w-4" />}<span className="truncate">{name || label}</span></div>
    </label>
  );
}
function IdUpload({ H, onNext }) {
  const [files, setFiles] = useState({}); const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState('stripe'); // 'stripe' | 'manual'
  const [idStatus, setIdStatus] = useState(null); // null | processing | verified | requires_input

  const startStripe = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/executor/release/identity/session`, {}, H());
      const stripe = await loadStripe(r.data.publishable_key);
      if (!stripe) { toast.error('Could not load Stripe'); setBusy(false); return; }
      const { error } = await stripe.verifyIdentity(r.data.client_secret);
      if (error) { toast.info('Verification was not completed'); setBusy(false); return; }
      setIdStatus('processing');
      let done = false;
      for (let i = 0; i < 8 && !done; i++) {
        await new Promise((res) => setTimeout(res, 2500));
        const s = (await axios.get(`${API}/executor/release/identity/status`, H())).data;
        if (s.verified) { setIdStatus('verified'); toast.success('Identity verified'); done = true; }
        else if (s.status === 'requires_input') { setIdStatus('requires_input'); toast.error(s.reason || 'Verification needs another try'); done = true; }
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start verification'); }
    finally { setBusy(false); }
  };

  const submitManual = async () => {
    if (!files.id_front || !files.id_back) { toast.error('Please upload the front and back of your ID.'); return; }
    setBusy(true);
    const fd = new FormData();
    Object.entries(files).forEach(([k, v]) => fd.append(k, v));
    fd.append('biometric_verified', sessionStorage.getItem('exec_biometric') || 'false');
    try { await axios.post(`${API}/executor/release/documents`, fd, H()); toast.success('Documents received'); onNext(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); setBusy(false); }
  };

  return (
    <div>
      <IdCard className="h-9 w-9 mb-2" style={{ color: C.navy }} />
      <h2 className="font-bold text-lg" style={{ color: C.ink }}>Government ID verification</h2>
      <p className="text-sm mt-1 mb-4" style={{ color: C.mute }}>Verify your government-issued ID. Instant verification uses Stripe Identity (secure ID + selfie match). You can also upload photos for manual review.</p>

      <div className="flex gap-2 mb-4 p-1 rounded-xl" style={{ background: '#F1EEE8' }} data-testid="id-mode-toggle">
        <button onClick={() => setMode('stripe')} className="flex-1 h-9 rounded-lg text-xs font-semibold" style={mode === 'stripe' ? { background: '#fff', color: C.navy } : { color: C.mute }} data-testid="id-mode-stripe">Instant (Stripe)</button>
        <button onClick={() => setMode('manual')} className="flex-1 h-9 rounded-lg text-xs font-semibold" style={mode === 'manual' ? { background: '#fff', color: C.navy } : { color: C.mute }} data-testid="id-mode-manual">Upload manually</button>
      </div>

      {mode === 'stripe' ? (
        <div>
          {idStatus === 'verified' ? (
            <div className="rounded-xl p-4 text-center" style={{ background: '#EAF3EE' }} data-testid="id-verified">
              <Check className="h-6 w-6 mx-auto mb-1" style={{ color: C.green }} />
              <p className="text-sm font-semibold" style={{ color: C.green }}>Identity verified</p>
            </div>
          ) : (
            <>
              <button onClick={startStripe} disabled={busy} className="w-full h-11 rounded-xl text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: C.navy }} data-testid="id-stripe-start">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} {idStatus === 'requires_input' ? 'Try verification again' : 'Verify my ID instantly'}
              </button>
              {idStatus === 'processing' && <p className="text-xs mt-2 text-center" style={{ color: C.gold }}>Verification is processing… you can continue once verified.</p>}
              {idStatus === 'requires_input' && <p className="text-xs mt-2 text-center" style={{ color: '#C0563F' }}>Verification needs another try, or switch to manual upload.</p>}
            </>
          )}
          <button onClick={onNext} disabled={idStatus !== 'verified'} className="mt-4 w-full h-11 rounded-xl border font-semibold disabled:opacity-40" style={{ borderColor: C.navy, color: C.navy }} data-testid="id-stripe-continue">Continue</button>
        </div>
      ) : (
        <div>
          <div className="space-y-2">
            <FileField label="Front of ID" onPick={(f) => setFiles((p) => ({ ...p, id_front: f }))} testid="id-front" />
            <FileField label="Back of ID" onPick={(f) => setFiles((p) => ({ ...p, id_back: f }))} testid="id-back" />
            <FileField label="Selfie (optional)" onPick={(f) => setFiles((p) => ({ ...p, selfie: f }))} testid="id-selfie" />
            <FileField label="Death certificate (optional)" onPick={(f) => setFiles((p) => ({ ...p, death_certificate: f }))} testid="id-death-cert" />
          </div>
          <button onClick={submitManual} disabled={busy} className="mt-4 w-full h-11 rounded-xl text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: C.navy }} data-testid="id-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Continue</button>
        </div>
      )}
    </div>
  );
}

function DeathCheck({ H, onNext }) {
  const [f, setF] = useState({ death_date: '', relationship: '', confirm_passed: false, confirm_authorized: false }); const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.death_date || !f.relationship) { toast.error('Please complete all fields.'); return; }
    if (!f.confirm_passed || !f.confirm_authorized) { toast.error('Please confirm both statements.'); return; }
    setBusy(true);
    try { await axios.post(`${API}/executor/release/details`, f, H()); onNext(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); setBusy(false); }
  };
  return (
    <div>
      <FileCheck2 className="h-9 w-9 mb-2" style={{ color: C.navy }} />
      <h2 className="font-bold text-lg" style={{ color: C.ink }}>Death confirmation</h2>
      <div className="space-y-3 mt-3">
        <div><label className="block text-xs font-semibold mb-1" style={{ color: '#57534E' }}>Date of passing</label><input type="date" value={f.death_date} onChange={(e) => set('death_date', e.target.value)} className={inp} data-testid="death-date" /></div>
        <div><label className="block text-xs font-semibold mb-1" style={{ color: '#57534E' }}>Your relationship to the deceased</label><input value={f.relationship} onChange={(e) => set('relationship', e.target.value)} className={inp} data-testid="death-relationship" /></div>
        <label className="flex items-start gap-2 text-sm" style={{ color: C.ink }}><input type="checkbox" checked={f.confirm_passed} onChange={(e) => set('confirm_passed', e.target.checked)} className="mt-0.5" data-testid="confirm-passed" /> I confirm the account holder has passed away.</label>
        <label className="flex items-start gap-2 text-sm" style={{ color: C.ink }}><input type="checkbox" checked={f.confirm_authorized} onChange={(e) => set('confirm_authorized', e.target.checked)} className="mt-0.5" data-testid="confirm-authorized" /> I confirm I am authorized to take this action.</label>
      </div>
      <button onClick={submit} disabled={busy} className="mt-4 w-full h-11 rounded-xl text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: C.navy }} data-testid="death-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Continue</button>
    </div>
  );
}

function Waiver({ H, onNext }) {
  const [name, setName] = useState(''); const [sig, setSig] = useState(''); const [agreed, setAgreed] = useState(false); const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!agreed || !name.trim() || !sig.trim()) { toast.error('Please sign and agree to continue.'); return; }
    setBusy(true);
    try { await axios.post(`${API}/executor/release/sign`, { full_legal_name: name, digital_signature: sig, agreed }, H()); onNext(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); setBusy(false); }
  };
  return (
    <div>
      <PenLine className="h-9 w-9 mb-2" style={{ color: C.navy }} />
      <h2 className="font-bold text-lg" style={{ color: C.ink }}>Terms &amp; Conditions Waiver</h2>
      <p className="text-sm rounded-xl p-3 my-3" style={{ background: '#FFF9EC', color: '#7a6526' }}>{WAIVER}</p>
      <div className="space-y-3">
        <div><label className="block text-xs font-semibold mb-1" style={{ color: '#57534E' }}>Type your full legal name</label><input value={name} onChange={(e) => setName(e.target.value)} className={inp} data-testid="waiver-name" /></div>
        <div><label className="block text-xs font-semibold mb-1" style={{ color: '#57534E' }}>Digital signature (type your name)</label><input value={sig} onChange={(e) => setSig(e.target.value)} className={inp} style={{ fontFamily: 'cursive' }} data-testid="waiver-sig" /></div>
        <label className="flex items-start gap-2 text-sm" style={{ color: C.ink }}><input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5" data-testid="waiver-agree" /> I have read and agree to the waiver above.</label>
      </div>
      <button onClick={submit} disabled={busy} className="mt-4 w-full h-11 rounded-xl text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: C.navy }} data-testid="waiver-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign &amp; continue</button>
    </div>
  );
}

function Confirm({ H, ctx, onReleased }) {
  const [busy, setBusy] = useState(false); const [ack, setAck] = useState(false);
  const release = async () => {
    if (!ack) { toast.error('Please acknowledge this action cannot be undone.'); return; }
    setBusy(true);
    try { const r = await axios.post(`${API}/executor/release/confirm`, {}, H()); toast.success(`Released to ${r.data.notifications_sent} beneficiaries`); onReleased(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Release failed'); setBusy(false); }
  };
  return (
    <div className="text-center">
      <div className="w-12 h-12 rounded-full mx-auto flex items-center justify-center mb-3" style={{ background: '#FCECE8' }}><AlertTriangle className="h-6 w-6" style={{ color: C.red }} /></div>
      <h2 className="font-bold text-lg" style={{ color: C.ink }}>Final confirmation</h2>
      <p className="text-sm mt-1 mb-1" style={{ color: C.mute }}>This will notify {ctx?.beneficiaries_to_notify ?? 'the selected'} beneficiaries with the saved account location and instructions.</p>
      <p className="text-sm font-bold mb-4" style={{ color: C.red }}>This action cannot be undone.</p>
      <label className="flex items-start gap-2 text-sm text-left mb-4" style={{ color: C.ink }}><input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5" data-testid="confirm-ack" /> I understand this action cannot be undone.</label>
      <button onClick={release} disabled={busy} className="w-full h-12 rounded-xl text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: C.red }} data-testid="release-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Release Information to Beneficiaries</button>
    </div>
  );
}

function Released() {
  return (
    <div className="bg-white rounded-2xl border border-[#EDE7DC] p-8 text-center" data-testid="release-complete">
      <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center mb-3" style={{ background: '#EAF3EE' }}><Check className="h-7 w-7" style={{ color: C.green }} /></div>
      <h2 className="font-bold text-xl" style={{ color: C.ink }}>Information released</h2>
      <p className="text-sm mt-2" style={{ color: C.mute }}>The selected beneficiaries have been notified with the saved account location and instructions. Scheduled Whispers and after-passing messages have begun. Thank you for your care.</p>
    </div>
  );
}
